import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { resetAllSlices } from '../../../redux/slice/ReduxResetSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import LanguageSelector from '../../../shared-components/language-selector/LanguageSelector';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { getCookie, setCookie } from '../../../shared-functions/Cookies';
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import {
  emailValidation,
  phoneNumberValidation,
  textAndNumberValidation,
} from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';

/**
 * Login component
 * @returns {React.JSX.Element} The rendered Login component.
 * @description Login component is a component for login functionality.
 */
function LoginComponent() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParameters] = useSearchParams();
  const accountCustomId = getCookie('accountCustomId');

  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    password: string;
    accountCustomId: string;
    type: string | number;
    countryCode: string;
  }>({
    emailOrPhoneNumber: '',
    password: '',
    accountCustomId: accountCustomId || '',
    type: 'email',
    countryCode: currentBranding === 'energic' ? '966' : '1',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * Function to handle the login success
   */
  const loginSuccessHandler = (response: any) => {
    // Store the verification status in local storage
    setLocalStorageItem('accountVerificationStatus', response?.accountVerificationStatus);

    // Store access token inside cookies
    setCookie('ev-access-token', response?.access?.token, null, response?.access?.expires);

    // Store refresh token inside cookies
    setCookie('ev-refresh-token', response?.refresh?.token, null, response?.refresh?.expires);

    const userVerification = {
      redirectToEmailVerification: response?.redirectToEmailVerification,
      redirectToPhoneVerification: response?.redirectToPhoneVerification,
      isInitialPasswordReset: response?.isInitialPasswordReset,
    };

    // Set verification in local storage to use in email verification page
    setLocalStorageItem('userVerification', userVerification);

    setFormStatus({
      showFormErrors: false,
      buttonLoader: false,
    });

    // Navigate to next page based on email and phone number verification
    if (response?.redirectToEmailVerification) {
      navigate('../login/email-verification');
    } else if (response?.redirectToPhoneVerification) {
      navigate('../login/phone-number-verification');
    } else if (!response?.isInitialPasswordReset) {
      navigate('../login/reset-initial-password');
    } else if (
      !response?.redirectToEmailVerification &&
      !response?.redirectToPhoneVerification &&
      response?.isInitialPasswordReset
    ) {
      removeLocalStorageItem('userVerification'); // If the user is verified remove the verification data in local storage
      if (response?.accountVerificationStatus === 'approved') {
        navigate('../ev-admin/dashboard');
      } else {
        navigate('../ev-admin/profile');
      }
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 401,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      textAndNumberValidation(20, 20, credentials.accountCustomId) &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
      // passwordValidation(credentials.password)
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const request = {
        countryCode: credentials.countryCode,
        accountCustomId: credentials.accountCustomId,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        password: credentials.password,
      };
      auth
        .login(request)
        .then((response) => {
          setCookie('accountCustomId', credentials.accountCustomId, null, null);
          loginSuccessHandler(response?.data);
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: 200, // Code is not required, if we pass code as 404 means then page will redirect to login (Just as refresh)
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    // Access specific query parameters
    const accessToken = searchParameters.get('accessToken');
    const refreshToken = searchParameters.get('refreshToken');
    const accountVerificationStatus = searchParameters.get('accountVerificationStatus');
    const accessTokenExpires = searchParameters.get('accessTokenExpires');
    const refreshTokenExpires = searchParameters.get('refreshTokenExpires');
    const redirectToEmailVerification = searchParameters.get('redirectToEmailVerification');
    const redirectToPhoneVerification = searchParameters.get('redirectToPhoneVerification');
    const isInitialPasswordReset = searchParameters.get('isInitialPasswordReset');

    if (
      accessToken &&
      refreshToken &&
      accountVerificationStatus &&
      accessTokenExpires &&
      refreshTokenExpires
    ) {
      loginSuccessHandler({
        accountVerificationStatus,
        access: {
          token: accessToken,
          expires: accessTokenExpires,
        },
        refresh: {
          token: refreshToken,
          expires: refreshTokenExpires,
        },
        redirectToEmailVerification,
        redirectToPhoneVerification,
        isInitialPasswordReset,
      });
    }
  }, [location.search, searchParameters]);

  useEffect(() => {
    dispatch(resetAllSlices());
    const accessToken = getCookie('ev-access-token');
    if (accessToken) {
      navigate('../ev-admin/dashboard');
    }
  }, []);

  return (
    <div className="login-component-v2" dir={i18n.dir()}>
      <div className="login-component-v2-header">
        <p className="title">{t('signIn')}</p>
      </div>
      <div className="login-component-v2-body">
        <CustomInput
          inputLabel={t('accountId')}
          type="text"
          placeholder={t('enterAccountId')}
          name="accountCustomId"
          value={credentials.accountCustomId}
          onChangeValue={handleCredentialChange}
          error={
            !textAndNumberValidation(20, 20, credentials.accountCustomId) &&
            formStatus.showFormErrors
          }
          errorMessage={t('invalidAccountId')}
        />
        <RadioButton
          name="type"
          value={credentials.type}
          list={[
            {
              label: `${t('email')}`,
              value: 'email',
            },
            {
              label: `${t('phoneNumber')}`,
              value: 'phone',
            },
          ]}
          onChangeValue={handleCredentialChange}
        />
        {credentials.type === 'email' ? (
          <CustomInput
            type="text"
            placeholder={t('enterEmail')}
            name="emailOrPhoneNumber"
            value={credentials.emailOrPhoneNumber}
            onChangeValue={handleCredentialChange}
            error={!emailValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors}
            errorMessage={t('invalidEmailId')}
          />
        ) : (
          <CustomPhoneNumber
            placeholder={t('enterPhoneNumber')}
            name="emailOrPhoneNumber"
            value={credentials.emailOrPhoneNumber}
            onChangeValue={handleCredentialChange}
            error={
              !phoneNumberValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors
            }
            errorMessage={t('invalidPhoneNumber')}
            countryCode={credentials.countryCode}
          />
        )}
        <div className="password-container">
          <CustomInput
            inputLabel={t('password')}
            type="password"
            placeholder={t('enterPassword')}
            name="password"
            value={credentials.password}
            onChangeValue={handleCredentialChange}
          />
        </div>
        <div className="forgot-password">
          <span role="presentation" onClick={() => navigate('../login/forgot-password')}>
            {t('forgotPassword')}
          </span>
        </div>
        <MyButton
          label={t('signIn')}
          buttonType="submit"
          onClickFunc={handleSubmit}
          loading={formStatus.buttonLoader}
          disableButton={formStatus.buttonLoader}
        />
        <div className="register-account">
          <span>{t('noAccount')}</span>
          <span
            className="register"
            role="presentation"
            onClick={() => navigate('../login/register')}>
            {t('signUp')}
          </span>
        </div>
      </div>
      <div className="language-selector">
        <LanguageSelector isCollapsed={false} />
      </div>
    </div>
  );
}

export default LoginComponent;
